import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { useToast } from '../../hooks/use-toast';
import { sysConfigApiService, type SysConfigEntry, type PowerEnvironmentConfig } from '../../../../../features/scopingAi/services/sysConfigApiService';
import { ENV_SCHEMES } from '../../../../../features/scopingAi/lib/envSchemeRegistry';
import {
  RETAILER_ALIASES,
  NON_ENERGY_DEFAULTS_FALLBACK,
  type NonEnergyDefaultsConfig,
} from '../../../../../features/scopingAi/lib/nonEnergyDefaultsRegistry';
import { Settings2, Zap, Receipt, Save, RefreshCw, AlertCircle, ChevronDown, ChevronUp, Code2, Plus, X } from 'lucide-react';

// ── PowerEnvironment editor — scheme × year grid ─────────────────────────────
//
// sys_config.powerEnvironment.value shape: { schemes: { "LREC": { "2026": 16.67, "2027": ... },
// "SREC": {...}, "VEEC/ESS/REES": {...}, "ESC": {...}, "PRC": {...}, "REPS": {...}, "EEIS": {...} } }
// — one row per registry scheme (envSchemeRegistry.ts is the single source of truth for which
// schemes exist), one column per configured year, so a scheme's default can change year to year
// instead of being one flat number forever. resolvePowerPct() (envSchemeRegistry.ts) is what
// every parse-time/display consumer calls to read a value back out of this shape.

type SchemeYearValues = Record<string, Record<string, string>>; // displayKey -> year -> string (controlled input)

const valuesToPayload = (values: SchemeYearValues): PowerEnvironmentConfig => {
  const payload: PowerEnvironmentConfig = {};
  Object.entries(values).forEach(([displayKey, years]) => {
    const yearEntries: Record<string, number> = {};
    Object.entries(years).forEach(([year, raw]) => {
      const num = parseFloat(raw);
      if (!isNaN(num)) yearEntries[year] = num;
    });
    if (Object.keys(yearEntries).length > 0) payload[displayKey] = yearEntries;
  });
  return payload;
};

const payloadToValues = (config: PowerEnvironmentConfig | undefined): SchemeYearValues => {
  const values: SchemeYearValues = {};
  ENV_SCHEMES.forEach(s => { values[s.displayKey] = {}; });
  if (config) {
    Object.entries(config).forEach(([displayKey, years]) => {
      if (!values[displayKey]) values[displayKey] = {}; // custom/unregistered scheme found in data — still show it
      Object.entries(years || {}).forEach(([year, pct]) => {
        values[displayKey][year] = String(pct);
      });
    });
  }
  return values;
};

const collectYears = (values: SchemeYearValues): string[] => {
  const years = new Set<string>();
  Object.values(values).forEach(yearMap => Object.keys(yearMap).forEach(y => years.add(y)));
  const sorted = Array.from(years).filter(y => /^\d+$/.test(y)).sort((a, b) => Number(a) - Number(b));
  return sorted.length > 0 ? sorted : [String(new Date().getFullYear())];
};

type PowerEnvEditorProps = {
  entry: SysConfigEntry | null;
  onSaved: (entry: SysConfigEntry) => void;
};

const PowerEnvEditor: React.FC<PowerEnvEditorProps> = ({ entry, onSaved }) => {
  const { toast } = useToast();
  const [values, setValues] = useState<SchemeYearValues>(() => payloadToValues(undefined));
  const [years, setYears] = useState<string[]>(() => collectYears(payloadToValues(undefined)));
  const [isSaving, setIsSaving] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!entry) return;
    const config = (entry.value as any)?.schemes as PowerEnvironmentConfig | undefined;
    const next = payloadToValues(config);
    setValues(next);
    setYears(collectYears(next));
    syncJson(next);
  }, [entry]);

  const syncJson = (v: SchemeYearValues) => {
    setJsonText(JSON.stringify({ schemes: valuesToPayload(v) }, null, 2));
    setJsonError(null);
  };

  const handleFieldChange = (displayKey: string, year: string, raw: string) => {
    const next = { ...values, [displayKey]: { ...values[displayKey], [year]: raw } };
    setValues(next);
    syncJson(next);
  };

  const handleAddYear = () => {
    const maxYear = years.length > 0 ? Math.max(...years.map(Number)) : new Date().getFullYear() - 1;
    const newYear = String(maxYear + 1);
    if (years.includes(newYear)) return;
    setYears(prev => [...prev, newYear].sort((a, b) => Number(a) - Number(b)));
    // Don't touch `values` — an added column with no entries yet is fine; typing into any
    // cell for that year is what actually adds it to each scheme's map on save.
  };

  const handleRemoveYear = (year: string) => {
    setYears(prev => prev.filter(y => y !== year));
    const next: SchemeYearValues = {};
    Object.entries(values).forEach(([displayKey, yearMap]) => {
      const { [year]: _removed, ...rest } = yearMap;
      next[displayKey] = rest;
    });
    setValues(next);
    syncJson(next);
  };

  const handleJsonChange = (raw: string) => {
    setJsonText(raw);
    try {
      const parsed = JSON.parse(raw) as { schemes?: PowerEnvironmentConfig };
      const next = payloadToValues(parsed.schemes);
      setValues(next);
      setYears(collectYears(next));
      setJsonError(null);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  const handleSave = async () => {
    if (jsonError) return;
    setIsSaving(true);
    const result = await sysConfigApiService.upsert(
      'powerEnvironment',
      { schemes: valuesToPayload(values) } as unknown as Record<string, unknown>,
      entry?.description ?? 'Year-wise fallback power percentages (%) per environmental certificate scheme. Applied at parse time when a retailer offer document does not include Power % for that scheme/year.'
    );
    setIsSaving(false);
    if (result.success && result.data) {
      onSaved(result.data);
      toast({ title: 'Saved', description: 'Power environment defaults updated.' });
    } else {
      toast({ title: 'Save failed', description: result.error, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      {/* Table editor */}
      {!showJson && (
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-2.5 px-4 font-semibold text-gray-600">Scheme</th>
                {years.map(year => (
                  <th key={year} className="text-center py-2.5 px-3 font-semibold text-gray-600 whitespace-nowrap">
                    <div className="flex items-center justify-center gap-1">
                      {year}
                      <button
                        type="button"
                        onClick={() => handleRemoveYear(year)}
                        className="text-gray-300 hover:text-red-500"
                        title={`Remove ${year} column`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </th>
                ))}
                <th className="py-2.5 px-3 w-10">
                  <button
                    type="button"
                    onClick={handleAddYear}
                    className="text-gray-400 hover:text-primary"
                    title="Add year"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ENV_SCHEMES.map(scheme => (
                <tr key={scheme.displayKey} className="hover:bg-gray-50/50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs font-semibold">{scheme.displayKey}</Badge>
                      <span className="text-gray-500 text-xs">{scheme.label}</span>
                      {scheme.states && (
                        <span className="text-gray-400 text-[10px] uppercase">{scheme.states.join('/')}</span>
                      )}
                    </div>
                  </td>
                  {years.map(year => (
                    <td key={year} className="py-3 px-3">
                      <div className="flex items-center justify-center gap-1">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={values[scheme.displayKey]?.[year] ?? ''}
                          onChange={e => handleFieldChange(scheme.displayKey, year, e.target.value)}
                          placeholder="—"
                          className="h-8 w-20 text-right font-mono text-sm"
                        />
                        <span className="text-gray-400 text-xs">%</span>
                      </div>
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Raw JSON editor */}
      {showJson && (
        <div className="space-y-1.5">
          <textarea
            value={jsonText}
            onChange={e => handleJsonChange(e.target.value)}
            rows={16}
            className={[
              'w-full font-mono text-xs p-3 rounded-lg border resize-y',
              jsonError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50',
            ].join(' ')}
            spellCheck={false}
          />
          {jsonError && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {jsonError}
            </p>
          )}
        </div>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={() => setShowJson(v => !v)}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
        >
          <Code2 className="w-3.5 h-3.5" />
          {showJson ? 'Switch to table view' : 'Edit as JSON'}
          {showJson ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        <Button
          onClick={handleSave}
          disabled={isSaving || !!jsonError}
          size="sm"
          className="gap-1.5"
        >
          {isSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {isSaving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

// ── Non-energy charge defaults editor — retailer × {SM, OSC} grid ────────────
//
// sys_config.non_energy_defaults.value shape:
//   { meterAgreement: 3996, retailers: { "origin": { supplementaryMetering, otherServiceCharge }, ... } }
//
// meterAgreement is ONE static value for every retailer (the SHIELD/Powermetric annual fee) —
// it is not per-retailer, so it gets its own field above the grid. Rows are the canonical
// retailer keys from nonEnergyDefaultsRegistry.ts, which maps the varying `retailer.name`
// spellings ("Origin energy", "EnergyAustralia", "FLO"/"Flo Energy Australia") onto one key.
// A blank cell is meaningful: that retailer has no default, so the field stays empty in offer
// review unless the offer itself states a value.

type RetailerFieldValues = Record<string, { sm: string; osc: string }>;

// Human-readable row label for a canonical key, derived from its first alias.
const RETAILER_LABELS: Record<string, string> = {
  origin: 'Origin Energy',
  energyaustralia: 'Energy Australia',
  momentum: 'Momentum Energy',
  agl: 'AGL',
  actewagl: 'ActewAGL',
  flo: 'Flo / Tango',
  engie: 'Engie',
  nextbusiness: 'Next Business Energy',
  smartest: 'Smartest Energy',
  nectr: 'Nectr',
  shell: 'Shell Energy',
};

const nonEnergyToValues = (config: NonEnergyDefaultsConfig | undefined): RetailerFieldValues => {
  const values: RetailerFieldValues = {};
  Object.keys(RETAILER_ALIASES).forEach(key => { values[key] = { sm: '', osc: '' }; });
  Object.entries(config?.retailers || {}).forEach(([key, d]) => {
    if (!values[key]) values[key] = { sm: '', osc: '' }; // unregistered key found in data — still show it
    if (typeof d?.supplementaryMetering === 'number') values[key].sm = String(d.supplementaryMetering);
    if (typeof d?.otherServiceCharge === 'number') values[key].osc = String(d.otherServiceCharge);
  });
  return values;
};

const valuesToNonEnergy = (values: RetailerFieldValues, meterAgreement: string): NonEnergyDefaultsConfig => {
  const retailers: NonEnergyDefaultsConfig['retailers'] = {};
  Object.entries(values).forEach(([key, { sm, osc }]) => {
    const entry: { supplementaryMetering?: number; otherServiceCharge?: number } = {};
    const smNum = parseFloat(sm);
    const oscNum = parseFloat(osc);
    if (!isNaN(smNum)) entry.supplementaryMetering = smNum;
    if (!isNaN(oscNum)) entry.otherServiceCharge = oscNum;
    // Keep retailers with no values as an empty object — an explicit "no defaults, leave
    // blank" entry (Shell) is different from a retailer that was never configured at all.
    retailers[key] = entry;
  });
  const ma = parseFloat(meterAgreement);
  return {
    meterAgreement: isNaN(ma) ? NON_ENERGY_DEFAULTS_FALLBACK.meterAgreement : ma,
    retailers,
  };
};

type NonEnergyEditorProps = {
  entry: SysConfigEntry | null;
  onSaved: (entry: SysConfigEntry) => void;
};

const NonEnergyEditor: React.FC<NonEnergyEditorProps> = ({ entry, onSaved }) => {
  const { toast } = useToast();
  const [values, setValues] = useState<RetailerFieldValues>(() => nonEnergyToValues(undefined));
  const [meterAgreement, setMeterAgreement] = useState<string>(String(NON_ENERGY_DEFAULTS_FALLBACK.meterAgreement));
  const [isSaving, setIsSaving] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!entry) return;
    const config = entry.value as unknown as NonEnergyDefaultsConfig | undefined;
    const next = nonEnergyToValues(config);
    const ma = typeof config?.meterAgreement === 'number'
      ? String(config.meterAgreement)
      : String(NON_ENERGY_DEFAULTS_FALLBACK.meterAgreement);
    setValues(next);
    setMeterAgreement(ma);
    syncJson(next, ma);
  }, [entry]);

  const syncJson = (v: RetailerFieldValues, ma: string) => {
    setJsonText(JSON.stringify(valuesToNonEnergy(v, ma), null, 2));
    setJsonError(null);
  };

  const handleFieldChange = (key: string, field: 'sm' | 'osc', raw: string) => {
    const next = { ...values, [key]: { ...values[key], [field]: raw } };
    setValues(next);
    syncJson(next, meterAgreement);
  };

  const handleMeterAgreementChange = (raw: string) => {
    setMeterAgreement(raw);
    syncJson(values, raw);
  };

  const handleJsonChange = (raw: string) => {
    setJsonText(raw);
    try {
      const parsed = JSON.parse(raw) as NonEnergyDefaultsConfig;
      setValues(nonEnergyToValues(parsed));
      setMeterAgreement(typeof parsed?.meterAgreement === 'number' ? String(parsed.meterAgreement) : '');
      setJsonError(null);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  const handleSave = async () => {
    if (jsonError) return;
    setIsSaving(true);
    const result = await sysConfigApiService.upsert(
      'non_energy_defaults',
      valuesToNonEnergy(values, meterAgreement) as unknown as Record<string, unknown>,
      entry?.description ?? 'Default non-energy charges. meterAgreement is static for all retailers; per-retailer supplementaryMetering / otherServiceCharge are offered via the wand button in offer review when the offer itself states no value.'
    );
    setIsSaving(false);
    if (result.success && result.data) {
      onSaved(result.data);
      toast({ title: 'Saved', description: 'Non-energy charge defaults updated.' });
    } else {
      toast({ title: 'Save failed', description: result.error, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      {!showJson && (
        <>
          {/* Meter Agreement — one value for every retailer */}
          <div className="flex items-center gap-3 p-3 rounded-lg border bg-gray-50/60">
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">Meter Agreement</div>
              <div className="text-xs text-gray-500">
                SHIELD / Powermetric annual fee — the same value for every retailer.
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-400 text-xs">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={meterAgreement}
                onChange={e => handleMeterAgreementChange(e.target.value)}
                className="h-8 w-28 text-right font-mono text-sm"
              />
              <span className="text-gray-400 text-xs">/yr</span>
            </div>
          </div>

          {/* Per-retailer SM / OSC */}
          <div className="rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-2.5 px-4 font-semibold text-gray-600">Retailer</th>
                  <th className="text-center py-2.5 px-3 font-semibold text-gray-600 whitespace-nowrap">
                    Supplementary Metering
                  </th>
                  <th className="text-center py-2.5 px-3 font-semibold text-gray-600 whitespace-nowrap">
                    Other Service Charge
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.keys(values).map(key => (
                  <tr key={key} className="hover:bg-gray-50/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs font-semibold">
                          {RETAILER_LABELS[key] || key}
                        </Badge>
                      </div>
                    </td>
                    {(['sm', 'osc'] as const).map(field => (
                      <td key={field} className="py-3 px-3">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-gray-400 text-xs">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={values[key]?.[field] ?? ''}
                            onChange={e => handleFieldChange(key, field, e.target.value)}
                            placeholder="—"
                            className="h-8 w-24 text-right font-mono text-sm"
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            A blank cell means that retailer has no default — the field stays empty in offer review
            unless the offer document states a value. Retailers not listed here (Aurora, Pacific
            Blue, Iberdrola, …) behave the same way.
          </p>
        </>
      )}

      {showJson && (
        <div className="space-y-1.5">
          <textarea
            value={jsonText}
            onChange={e => handleJsonChange(e.target.value)}
            rows={16}
            className={[
              'w-full font-mono text-xs p-3 rounded-lg border resize-y',
              jsonError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50',
            ].join(' ')}
            spellCheck={false}
          />
          {jsonError && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {jsonError}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={() => setShowJson(v => !v)}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
        >
          <Code2 className="w-3.5 h-3.5" />
          {showJson ? 'Switch to table view' : 'Edit as JSON'}
          {showJson ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        <Button onClick={handleSave} disabled={isSaving || !!jsonError} size="sm" className="gap-1.5">
          {isSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {isSaving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

// ── Main SystemConfig page ────────────────────────────────────────────────────

export const SystemConfig: React.FC = () => {
  const [entries, setEntries] = useState<SysConfigEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setLoadError(null);
    const result = await sysConfigApiService.list();
    setIsLoading(false);
    if (result.success) {
      setEntries(result.data || []);
    } else {
      setLoadError(result.error || 'Failed to load configuration');
    }
  };

  useEffect(() => { load(); }, []);

  const handleSaved = (updated: SysConfigEntry) => {
    setEntries(prev => {
      const idx = prev.findIndex(e => e.name === updated.name);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = updated;
        return next;
      }
      return [...prev, updated];
    });
  };

  const powerEnvEntry = entries.find(e => e.name === 'powerEnvironment') ?? null;
  const nonEnergyEntry = entries.find(e => e.name === 'non_energy_defaults') ?? null;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-primary" />
          System Configuration
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage global system defaults. Changes take effect for newly parsed offers.
        </p>
      </div>

      {loadError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading configuration…
        </div>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-green-600" />
              <CardTitle className="text-base">Environmental Power % Defaults</CardTitle>
            </div>
            <CardDescription>
              Fallback power percentages per scheme, per year. Applied at parse time when an offer
              document doesn't include Power % for a scheme — resolved using the exact contract
              year if configured, otherwise the nearest earlier configured year.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PowerEnvEditor entry={powerEnvEntry} onSaved={handleSaved} />
          </CardContent>
        </Card>
      )}

      {!isLoading && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-blue-600" />
              <CardTitle className="text-base">Non-Energy Charge Defaults</CardTitle>
            </div>
            <CardDescription>
              Meter Agreement is a single static fee applied to every retailer. Supplementary
              Metering and Other Service Charge vary by retailer — offered via the wand button in
              offer review when the offer document doesn't state a value of its own.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <NonEnergyEditor entry={nonEnergyEntry} onSaved={handleSaved} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};
